/**
 * Treatment plan QuestionnaireResponse domain model.
 *
 * The diagnosis & treatment plan template is a nested Questionnaire with repeating
 * groups (diagnosis → behavioral definitions / goals → objectives). Items are
 * identified by linkId *suffix* (template ids are prefixed with the template slug):
 *
 *   -plan-info                       group
 *     -treatment-frequency           string
 *     -date-assigned                 dateTime
 *     -expiration-date               dateTime
 *   -diagnosis                       group [repeats]
 *     -diagnosis-code                string ("CODE - description")
 *     -behavioral-definition         group [repeats]
 *       -behavioral-definition-text  text
 *     -goal                          group [repeats]
 *       -goal-text                   text
 *       -objective                   group [repeats]
 *         -objective-text            text
 *         -interventions             text
 *
 * This module provides:
 * - a repeat-aware parser into {@link ParsedTreatmentPlanDraft}
 * - deterministic assigned/expiration date helpers (180-day default; 90-day for adjustment disorder)
 * - the searchable expiration extension synchronizer
 * - minimal AI generation provenance helpers
 */

import type {
  Extension,
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
} from '@medplum/fhirtypes';

import {
  AI_TREATMENT_PLAN_GENERATION_PROVENANCE_EXT,
  TREATMENT_PLAN_EXPIRES_AT_EXT,
} from '../constants';

// ═══════════════════════════════════════════════════════════════════════
//  LinkId suffixes
// ═══════════════════════════════════════════════════════════════════════

export const TREATMENT_PLAN_LINK_ID_SUFFIXES = {
  planInfo: '-plan-info',
  treatmentFrequency: '-treatment-frequency',
  dateAssigned: '-date-assigned',
  expirationDate: '-expiration-date',
  presentingProblem: '-presenting-problem',
  presentingProblemText: '-presenting-problem-text',
  diagnosis: '-diagnosis',
  diagnosisCode: '-diagnosis-code',
  behavioralDefinition: '-behavioral-definition',
  behavioralDefinitionText: '-behavioral-definition-text',
  goal: '-goal',
  goalText: '-goal-text',
  objective: '-objective',
  objectiveText: '-objective-text',
  interventions: '-interventions',
} as const;

/** Default days between assignment and expiration (non–adjustment-disorder). */
export const TREATMENT_PLAN_EXPIRATION_DAYS = 180;

/** Renewal cycle for adjustment disorder (F43.2x) — shorter than default. */
export const TREATMENT_PLAN_EXPIRATION_DAYS_ADJUSTMENT_DISORDER = 90;

/**
 * Extract the ICD/DSM token from a stored diagnosis string (`"F43.23 - …"` → `F43.23`).
 */
export function extractDiagnosisCodeToken(codeOrDisplay: string | undefined | null): string {
  if (!codeOrDisplay?.trim()) return '';
  return codeOrDisplay.split(/\s*[-–—]\s*/)[0]!.trim().toUpperCase();
}

/** True for adjustment disorder codes (F43.2 / F43.20–F43.29). */
export function isAdjustmentDisorderDiagnosisCode(codeOrDisplay: string | undefined | null): boolean {
  const token = extractDiagnosisCodeToken(codeOrDisplay);
  return /^F43\.2\d?$/.test(token);
}

/**
 * Days until expiration for a plan given its diagnosis code displays.
 * Any adjustment-disorder diagnosis → 90; otherwise → 180.
 */
export function resolveTreatmentPlanExpirationDaysFromDiagnoses(
  diagnosisCodes: Array<string | undefined | null>,
): number {
  if (diagnosisCodes.some((c) => isAdjustmentDisorderDiagnosisCode(c))) {
    return TREATMENT_PLAN_EXPIRATION_DAYS_ADJUSTMENT_DISORDER;
  }
  return TREATMENT_PLAN_EXPIRATION_DAYS;
}

/** Resolve expiration days from QR items (parses `-diagnosis-code` answers). */
export function resolveTreatmentPlanExpirationDays(
  items: QuestionnaireResponseItem[] | undefined,
): number {
  const draft = parseTreatmentPlanDraft(items);
  return resolveTreatmentPlanExpirationDaysFromDiagnoses(draft.diagnoses.map((d) => d.code));
}

// ═══════════════════════════════════════════════════════════════════════
//  Parsed domain representation
// ═══════════════════════════════════════════════════════════════════════

export interface ParsedTreatmentPlanObjective {
  /** 0-based index among objective repeats under the parent goal. */
  responseIndex: number;
  text?: string;
  interventions?: string;
}

export interface ParsedTreatmentPlanGoal {
  /** 0-based index among goal repeats under the parent diagnosis. */
  responseIndex: number;
  text?: string;
  objectives: ParsedTreatmentPlanObjective[];
}

export interface ParsedTreatmentPlanBehavioralDefinition {
  /** 0-based index among behavioral-definition repeats under the parent diagnosis. */
  responseIndex: number;
  text?: string;
}

export interface ParsedTreatmentPlanDiagnosis {
  /** 0-based index among diagnosis group repeats. */
  responseIndex: number;
  /** Raw stored value, typically "CODE - description". */
  code?: string;
  behavioralDefinitions: ParsedTreatmentPlanBehavioralDefinition[];
  goals: ParsedTreatmentPlanGoal[];
}

export interface ParsedTreatmentPlanDraft {
  treatmentFrequency?: string;
  presentingProblem?: string;
  /** FHIR ISO dateTime string from the visible assigned answer. */
  assignedAt?: string;
  /** FHIR ISO dateTime string from the visible expiration answer. */
  expiresAt?: string;
  diagnoses: ParsedTreatmentPlanDiagnosis[];
}

// ═══════════════════════════════════════════════════════════════════════
//  Parsing
// ═══════════════════════════════════════════════════════════════════════

function childBySuffix(
  item: QuestionnaireResponseItem | undefined,
  suffix: string,
): QuestionnaireResponseItem | undefined {
  return item?.item?.find((child) => child.linkId?.endsWith(suffix));
}

function childrenBySuffix(
  item: QuestionnaireResponseItem | undefined,
  suffix: string,
): QuestionnaireResponseItem[] {
  return (item?.item ?? []).filter((child) => child.linkId?.endsWith(suffix));
}

function answerString(item: QuestionnaireResponseItem | undefined): string | undefined {
  const value = item?.answer?.[0]?.valueString?.trim();
  return value || undefined;
}

function answerDateTime(item: QuestionnaireResponseItem | undefined): string | undefined {
  const value = item?.answer?.[0]?.valueDateTime?.trim();
  return value || undefined;
}

function firstAnswerStringBySuffix(
  items: QuestionnaireResponseItem[] | undefined,
  suffixes: readonly string[],
): string | undefined {
  for (const item of items ?? []) {
    if (suffixes.some((suffix) => item.linkId?.endsWith(suffix))) {
      const value = answerString(item);
      if (value) return value;
    }
    const nested = firstAnswerStringBySuffix(item.item, suffixes);
    if (nested) return nested;
  }
  return undefined;
}

function parseObjective(
  objectiveItem: QuestionnaireResponseItem,
  responseIndex: number,
): ParsedTreatmentPlanObjective {
  const s = TREATMENT_PLAN_LINK_ID_SUFFIXES;
  return {
    responseIndex,
    text: answerString(childBySuffix(objectiveItem, s.objectiveText)),
    interventions: answerString(childBySuffix(objectiveItem, s.interventions)),
  };
}

function parseGoal(goalItem: QuestionnaireResponseItem, responseIndex: number): ParsedTreatmentPlanGoal {
  const s = TREATMENT_PLAN_LINK_ID_SUFFIXES;
  return {
    responseIndex,
    text: answerString(childBySuffix(goalItem, s.goalText)),
    objectives: childrenBySuffix(goalItem, s.objective).map(parseObjective),
  };
}

function parseDiagnosis(
  diagnosisItem: QuestionnaireResponseItem,
  responseIndex: number,
): ParsedTreatmentPlanDiagnosis {
  const s = TREATMENT_PLAN_LINK_ID_SUFFIXES;
  return {
    responseIndex,
    code: answerString(childBySuffix(diagnosisItem, s.diagnosisCode)),
    behavioralDefinitions: childrenBySuffix(diagnosisItem, s.behavioralDefinition).map(
      (item, index): ParsedTreatmentPlanBehavioralDefinition => ({
        responseIndex: index,
        text: answerString(childBySuffix(item, s.behavioralDefinitionText)),
      }),
    ),
    goals: childrenBySuffix(diagnosisItem, s.goal).map(parseGoal),
  };
}

/**
 * Parse treatment-plan QuestionnaireResponse items into a repeat-aware domain
 * representation. Hierarchy and repeat-instance order are preserved; nothing is
 * flattened across diagnoses and no synthetic dates/statuses are invented.
 */
export function parseTreatmentPlanDraft(
  items: QuestionnaireResponseItem[] | undefined,
): ParsedTreatmentPlanDraft {
  const s = TREATMENT_PLAN_LINK_ID_SUFFIXES;
  const planInfo = (items ?? []).find((item) => item.linkId?.endsWith(s.planInfo));
  const diagnoses = (items ?? []).filter((item) => item.linkId?.endsWith(s.diagnosis));
  return {
    treatmentFrequency: answerString(childBySuffix(planInfo, s.treatmentFrequency)),
    presentingProblem: firstAnswerStringBySuffix(items, [
      s.presentingProblem,
      s.presentingProblemText,
    ]),
    assignedAt: answerDateTime(childBySuffix(planInfo, s.dateAssigned)),
    expiresAt: answerDateTime(childBySuffix(planInfo, s.expirationDate)),
    diagnoses: diagnoses.map(parseDiagnosis),
  };
}

/**
 * Prefer an existing response item prefix so rebuilt items match the template.
 * Falls back to the Meridian Diagnosis & Treatment Plan slug.
 */
export function resolveTreatmentPlanLinkIdPrefix(
  items: QuestionnaireResponseItem[] | undefined,
  questionnaireRef?: string,
): string {
  const suffixes = Object.values(TREATMENT_PLAN_LINK_ID_SUFFIXES);
  for (const item of items ?? []) {
    const linkId = item.linkId;
    if (!linkId) continue;
    for (const suffix of suffixes) {
      if (linkId.endsWith(suffix)) return linkId.slice(0, -suffix.length);
    }
  }
  if (questionnaireRef) {
    const withoutVersion = questionnaireRef.split('|')[0] ?? questionnaireRef;
    const slug = withoutVersion.split('/').pop()?.trim();
    if (slug) return slug;
  }
  return 'meridian-counseling-diagnosis-and-treatment-plan';
}

function stringAnswers(value: string): NonNullable<QuestionnaireResponseItem['answer']> {
  return [{ valueString: value }];
}

function dateTimeAnswers(value: string): NonNullable<QuestionnaireResponseItem['answer']> {
  return [{ valueDateTime: value }];
}

/**
 * Rebuild QuestionnaireResponse.item from a CarePlan-backed draft so the form
 * displays the same clinical content as the CarePlan source of truth.
 */
export function buildTreatmentPlanItemsFromDraft(
  draft: ParsedTreatmentPlanDraft,
  linkIdPrefix: string,
): QuestionnaireResponseItem[] {
  const s = TREATMENT_PLAN_LINK_ID_SUFFIXES;
  const lid = (suffix: string) => `${linkIdPrefix}${suffix}`;

  const planInfoChildren: QuestionnaireResponseItem[] = [];
  if (draft.treatmentFrequency) {
    planInfoChildren.push({
      linkId: lid(s.treatmentFrequency),
      text: 'Treatment frequency',
      answer: stringAnswers(draft.treatmentFrequency),
    });
  }
  if (draft.assignedAt) {
    planInfoChildren.push({
      linkId: lid(s.dateAssigned),
      text: 'Date and time assigned to plan',
      answer: dateTimeAnswers(draft.assignedAt),
    });
  }
  if (draft.expiresAt) {
    planInfoChildren.push({
      linkId: lid(s.expirationDate),
      text: 'Expiration date and time',
      answer: dateTimeAnswers(draft.expiresAt),
    });
  }

  const diagnoses: QuestionnaireResponseItem[] = draft.diagnoses.map((dx) => {
    const children: QuestionnaireResponseItem[] = [];
    if (dx.code) {
      children.push({
        linkId: lid(s.diagnosisCode),
        text: 'Diagnosis',
        answer: stringAnswers(dx.code),
      });
    }
    if (draft.presentingProblem && dx.responseIndex === 0) {
      children.push({
        linkId: lid(s.presentingProblem),
        text: 'Presenting problem',
        answer: stringAnswers(draft.presentingProblem),
      });
    }
    for (const symptom of dx.behavioralDefinitions) {
      if (!symptom.text) continue;
      children.push({
        linkId: lid(s.behavioralDefinition),
        text: 'Symptoms',
        item: [{
          linkId: lid(s.behavioralDefinitionText),
          answer: stringAnswers(symptom.text),
        }],
      });
    }
    for (const goal of dx.goals) {
      const goalChildren: QuestionnaireResponseItem[] = [];
      if (goal.text) {
        goalChildren.push({
          linkId: lid(s.goalText),
          answer: stringAnswers(goal.text),
        });
      }
      for (const objective of goal.objectives) {
        const objectiveChildren: QuestionnaireResponseItem[] = [];
        if (objective.text) {
          objectiveChildren.push({
            linkId: lid(s.objectiveText),
            answer: stringAnswers(objective.text),
          });
        }
        if (objective.interventions) {
          objectiveChildren.push({
            linkId: lid(s.interventions),
            answer: stringAnswers(objective.interventions),
          });
        }
        goalChildren.push({
          linkId: lid(s.objective),
          item: objectiveChildren,
        });
      }
      children.push({
        linkId: lid(s.goal),
        item: goalChildren,
      });
    }
    return {
      linkId: lid(s.diagnosis),
      text: 'Diagnosis',
      item: children,
    };
  });

  const items: QuestionnaireResponseItem[] = [];
  if (planInfoChildren.length > 0) {
    items.push({
      linkId: lid(s.planInfo),
      text: 'Plan Information',
      item: planInfoChildren,
    });
  }
  items.push(...diagnoses);
  return items;
}

// ═══════════════════════════════════════════════════════════════════════
//  Assigned / expiration dates
// ═══════════════════════════════════════════════════════════════════════

/** Add whole calendar days to a FHIR ISO dateTime (UTC-day arithmetic; deterministic). */
export function addCalendarDays(iso: string, days: number): string {
  const date = new Date(iso);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString();
}

export function getTreatmentPlanAssignedAt(
  items: QuestionnaireResponseItem[] | undefined,
): string | undefined {
  return parseTreatmentPlanDraft(items).assignedAt;
}

export function getTreatmentPlanExpiresAt(
  items: QuestionnaireResponseItem[] | undefined,
): string | undefined {
  return parseTreatmentPlanDraft(items).expiresAt;
}

function findQuestionnaireItemBySuffix(
  items: QuestionnaireItem[] | undefined,
  suffix: string,
): QuestionnaireItem | undefined {
  for (const item of items ?? []) {
    if (item.linkId?.endsWith(suffix)) return item;
    const nested = findQuestionnaireItemBySuffix(item.item, suffix);
    if (nested) return nested;
  }
  return undefined;
}

/**
 * Set assigned/expiration valueDateTime answers on treatment-plan items, creating the
 * plan-info group and date items when missing. Uses the questionnaire to resolve the
 * template-prefixed linkIds. Returns a new items array; the input is not mutated.
 */
export function setTreatmentPlanDateAnswers(
  questionnaire: Questionnaire,
  items: QuestionnaireResponseItem[] | undefined,
  dates: { assignedAt?: string; expiresAt?: string },
): QuestionnaireResponseItem[] {
  const s = TREATMENT_PLAN_LINK_ID_SUFFIXES;
  const planInfoQ = findQuestionnaireItemBySuffix(questionnaire.item, s.planInfo);
  const assignedQ = findQuestionnaireItemBySuffix(questionnaire.item, s.dateAssigned);
  const expirationQ = findQuestionnaireItemBySuffix(questionnaire.item, s.expirationDate);
  if (!planInfoQ?.linkId) return [...(items ?? [])];

  const next = (items ?? []).map((item) => ({ ...item, item: item.item ? [...item.item] : item.item }));
  let planInfo = next.find((item) => item.linkId === planInfoQ.linkId);
  if (!planInfo) {
    planInfo = { linkId: planInfoQ.linkId, text: planInfoQ.text, item: [] };
    next.unshift(planInfo);
  }
  planInfo.item = planInfo.item ? [...planInfo.item] : [];

  const setDate = (qItem: QuestionnaireItem | undefined, value: string | undefined): void => {
    if (!qItem?.linkId || value === undefined || !planInfo) return;
    const existingIndex = planInfo.item!.findIndex((child) => child.linkId === qItem.linkId);
    const node: QuestionnaireResponseItem = {
      ...(existingIndex >= 0 ? planInfo.item![existingIndex] : { linkId: qItem.linkId, text: qItem.text }),
      answer: [{ valueDateTime: value }],
    };
    if (existingIndex >= 0) {
      planInfo.item![existingIndex] = node;
    } else {
      planInfo.item!.push(node);
    }
  };

  setDate(assignedQ, dates.assignedAt);
  setDate(expirationQ, dates.expiresAt);
  return next;
}

/**
 * Populate assigned/expiration defaults for a new or incomplete treatment plan:
 * assigned = now, expiration = assigned + diagnosis-aware calendar days
 * (180 default; 90 for adjustment disorder). Populates only absent fields —
 * provider-entered values are never overwritten. Deterministic application
 * logic; never computed by the LLM.
 */
export function initializeTreatmentPlanDates(
  questionnaire: Questionnaire,
  items: QuestionnaireResponseItem[] | undefined,
  now: Date = new Date(),
): QuestionnaireResponseItem[] {
  const current = parseTreatmentPlanDraft(items);
  if (current.assignedAt && current.expiresAt) return [...(items ?? [])];
  const assignedAt = current.assignedAt ?? now.toISOString();
  const days = resolveTreatmentPlanExpirationDays(items);
  const expiresAt = current.expiresAt ?? addCalendarDays(assignedAt, days);
  return setTreatmentPlanDateAnswers(questionnaire, items, {
    ...(current.assignedAt ? {} : { assignedAt }),
    ...(current.expiresAt ? {} : { expiresAt }),
  });
}

export interface TreatmentPlanDateValidationResult {
  valid: boolean;
  errors: string[];
}

function parseableDate(value: string | undefined): Date | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

/** Sign-time validation: both dates must exist, be parseable, and expiration must follow assignment. */
export function validateTreatmentPlanDates(
  items: QuestionnaireResponseItem[] | undefined,
): TreatmentPlanDateValidationResult {
  const { assignedAt, expiresAt } = parseTreatmentPlanDraft(items);
  const errors: string[] = [];
  const assigned = parseableDate(assignedAt);
  const expires = parseableDate(expiresAt);
  if (!assignedAt) {
    errors.push('Date and time assigned to plan is required.');
  } else if (!assigned) {
    errors.push('Date and time assigned to plan is not a valid date.');
  }
  if (!expiresAt) {
    errors.push('Expiration date and time is required.');
  } else if (!expires) {
    errors.push('Expiration date and time is not a valid date.');
  }
  if (assigned && expires && expires.getTime() <= assigned.getTime()) {
    errors.push('Expiration date and time must be later than the assigned date and time.');
  }
  return { valid: errors.length === 0, errors };
}

// ═══════════════════════════════════════════════════════════════════════
//  Searchable expiration extension
// ═══════════════════════════════════════════════════════════════════════

function upsertExtension(extensions: Extension[] | undefined, ext: Extension): Extension[] {
  const rest = (extensions ?? []).filter((e) => e.url !== ext.url);
  return [...rest, ext];
}

/**
 * Mirror the visible expiration answer into the searchable
 * {@link TREATMENT_PLAN_EXPIRES_AT_EXT} extension. Call before every treatment-plan
 * save. Removes the extension when no visible expiration answer exists.
 */
export function syncTreatmentPlanExpirationExtension(qr: QuestionnaireResponse): QuestionnaireResponse {
  const expiresAt = getTreatmentPlanExpiresAt(qr.item);
  if (!expiresAt) {
    const rest = (qr.extension ?? []).filter((e) => e.url !== TREATMENT_PLAN_EXPIRES_AT_EXT);
    return { ...qr, extension: rest.length ? rest : undefined };
  }
  return {
    ...qr,
    extension: upsertExtension(qr.extension, {
      url: TREATMENT_PLAN_EXPIRES_AT_EXT,
      valueDateTime: expiresAt,
    }),
  };
}

export function getTreatmentPlanExpirationExtension(qr: QuestionnaireResponse): string | undefined {
  return qr.extension?.find((e) => e.url === TREATMENT_PLAN_EXPIRES_AT_EXT)?.valueDateTime;
}

// ═══════════════════════════════════════════════════════════════════════
//  AI generation provenance
// ═══════════════════════════════════════════════════════════════════════

export interface TreatmentPlanGenerationProvenance {
  generator: 'ai-treatment-plan-generator';
  /** ISO timestamp when the plan structure was generated. */
  generatedAt: string;
  model?: string;
  promptVersion: string;
  /** Always false at generation time — provider review/signing happens in the editor. */
  providerReviewed: false;
}

/** Persist minimal AI generation provenance. Never include transcript or patient narrative. */
export function setTreatmentPlanGenerationProvenance(
  qr: QuestionnaireResponse,
  provenance: TreatmentPlanGenerationProvenance,
): QuestionnaireResponse {
  return {
    ...qr,
    extension: upsertExtension(qr.extension, {
      url: AI_TREATMENT_PLAN_GENERATION_PROVENANCE_EXT,
      valueString: JSON.stringify(provenance),
    }),
  };
}

export function getTreatmentPlanGenerationProvenance(
  qr: QuestionnaireResponse,
): TreatmentPlanGenerationProvenance | undefined {
  const raw = qr.extension?.find((e) => e.url === AI_TREATMENT_PLAN_GENERATION_PROVENANCE_EXT)?.valueString;
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as Partial<TreatmentPlanGenerationProvenance>;
    if (parsed?.generator !== 'ai-treatment-plan-generator') return undefined;
    if (typeof parsed.generatedAt !== 'string' || typeof parsed.promptVersion !== 'string') {
      return undefined;
    }
    return {
      generator: 'ai-treatment-plan-generator',
      generatedAt: parsed.generatedAt,
      model: typeof parsed.model === 'string' ? parsed.model : undefined,
      promptVersion: parsed.promptVersion,
      providerReviewed: false,
    };
  } catch {
    return undefined;
  }
}
